import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Form, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import {
  useExpenseByIdQuery,
  useUpdateExpenseItemMutation,
  Expense,
  UpdateExpenseItemMutationVariables,
} from '../../../__generated__/graphql';
import { useToasts } from '../../../../../context/Toast';
import { normalizeTimeToHHmm } from '../../../../../utils/time';
import { useCurrencyFormatter } from '../../../../../hooks/useCurrencyFormatter';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { collectFormDataExtraFields } from '../../../components/ExtraFields/logic';
import { recordTypeFormKey } from '../../../components/ExtraFields/logic';
import {
  ExtraFieldsSupportedObject,
  useObjectExtraFields,
} from '../../../components/ExtraFields';
import { validateExpense } from '../expenseValidation';
import {
  useExpenseFormState,
  useExpenseDropdownOptions,
  useExpenseSubmit,
  useMerchantField,
} from './hooks';
import {
  DynamicExpenseForm,
  SharedFormButtons,
  FinalAmountDisplay,
  ExpenseFormWrapper,
} from './components';
import { ExpenseWrapperProps } from './types';
import * as yup from 'yup';
import './ExpenseSaveModal.css';
import {
  AUTO_SAVE_DELAY_MS,
  type MealType,
  type MealAllowance,
  type MealDeductions,
  getMealExpenseItem,
  getMealPercentages,
  hasMealAllowanceRules,
} from './perDiemHelpers';
import {
  initializeMealAllowances,
  calculateIncludedPercentage,
  calculateMealDeductions,
  buildMealAllowanceUpdateInput,
} from './perDiemHelpers';
import { ExpenseRecordType } from '../types';

export const PER_DIEM_RECORD_TYPE_KEY = 'Per_Diem';

type FormValues = {
  [recordTypeFormKey]: string;
  description: string;
  fromDate: Date;
  toDate?: Date | null;
  fromTime: string;
  toTime: string;
  expenseGroup?: string | null;
  project?: string | null;
  category?: string | null;
  merchant?: string | null;
  expenseGroupId: string;
  projectId: string;
  categoryId: string;
  merchantId: string;
} & Record<string, unknown>;

type Props = ExpenseWrapperProps;

const mapExpenseToFormData = (
  expense: NonNullable<
    import('../../../__generated__/graphql').ExpenseByIdQuery['expense']
  >,
): FormValues => {
  const expenseGroupId = expense.flair__Expense_Group__c || '';
  const projectId = expense.project?.Id || '';
  const categoryId = expense.expenseCategory?.Id || '';
  const merchantId = expense.merchant?.Id || '';

  return {
    [recordTypeFormKey]: PER_DIEM_RECORD_TYPE_KEY,
    description: expense.flair__Description__c || '',
    fromDate: expense.flair__Date_From__c
      ? new Date(expense.flair__Date_From__c)
      : new Date(),
    toDate: expense.flair__Date_To__c
      ? new Date(expense.flair__Date_To__c)
      : null,
    fromTime: normalizeTimeToHHmm(expense.flair__Time_From__c) || '',
    toTime: normalizeTimeToHHmm(expense.flair__Time_To__c) || '',
    expenseGroup: expenseGroupId || null,
    project: projectId || null,
    category: categoryId || null,
    merchant: merchantId || null,
    expenseGroupId,
    projectId,
    categoryId,
    merchantId,
  };
};

const PerDiemFormContent: React.FC<Props> = ({
  onSave,
  onCancel,
  expenseGroupId,
  expenseId,
  loading = false,
  expenseFormData,
}) => {
  const { t } = useTranslation();
  const { addError } = useToasts();
  const formatCurrency = useCurrencyFormatter();
  const { watch } = useFormContext();
  const { data: expenseData } = useExpenseByIdQuery({
    variables: { id: expenseId || '' },
    skip: !expenseId,
  });

  const [updateExpenseItemMutation] = useUpdateExpenseItemMutation();

  const {
    formData,
    setFormData,
    handleInputChange,
    isUpdatingExpense,
    hasLoadedInitialData,
    setHasLoadedInitialData,
    hasDataChanged,
    autoSaveExpense,
    updateExpenseWithMerchant,
    lastSavedDataRef,
  } = useExpenseFormState<FormValues>({
    expenseId: expenseId || null,
    initialData: {
      [recordTypeFormKey]: PER_DIEM_RECORD_TYPE_KEY,
      description: '',
      fromDate: new Date(),
      toDate: null,
      fromTime: '',
      toTime: '',
      expenseGroup: expenseGroupId || null,
      project: null,
      category: null,
      merchant: null,
      expenseGroupId: expenseGroupId || '',
      projectId: '',
      categoryId: '',
      merchantId: '',
    },
  });

  const [customMerchantOptions, setCustomMerchantOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const allFormValuesWatched = watch();
  const allFormValuesRef = useRef(allFormValuesWatched);
  allFormValuesRef.current = allFormValuesWatched;

  const { handleCreateMerchant } = useMerchantField({
    onMerchantChange: (merchantId: string) =>
      handleInputChange('merchantId', merchantId),
    updateExpenseWithMerchant,
    setMerchantOptions: setCustomMerchantOptions,
    merchantOptions: customMerchantOptions,
  });

  const {
    expenseGroupOptions,
    projectOptions,
    categoryOptions,
    merchantOptions,
  } = useExpenseDropdownOptions(customMerchantOptions, expenseFormData);

  const { getFormFields } = useObjectExtraFields(
    ExtraFieldsSupportedObject.Expense,
  );

  const defaultMealAllowances = useMemo(
    () => initializeMealAllowances(null, t),
    [t],
  );

  const [mealAllowances, setMealAllowances] = useState<MealAllowance[]>(
    defaultMealAllowances,
  );
  const [previousMealAllowances, setPreviousMealAllowances] = useState<
    MealAllowance[]
  >(defaultMealAllowances);

  const [isUpdatingExpenseItem, setIsUpdatingExpenseItem] =
    useState<boolean>(false);
  const isFirstRenderAfterLoadRef = useRef<boolean>(true);
  const previousExtraFieldsRef = useRef<string>('');

  const isLoading = loading || isUpdatingExpense;

  const validateExpenseCallback = useCallback(() => {
    const allFormValues = watch();
    const descriptionValue =
      allFormValues.description && allFormValues.description.trim() !== ''
        ? allFormValues.description
        : formData.description && formData.description.trim() !== ''
        ? formData.description
        : '';
    const merchantIdValue =
      allFormValues.merchantId || formData.merchantId || '';
    const categoryIdValue =
      allFormValues.categoryId || formData.categoryId || '';
    const fromDateValue = allFormValues.fromDate || formData.fromDate;
    const formDataWithExtraFields = {
      ...formData,
      ...allFormValues,
      description: descriptionValue,
      merchantId: merchantIdValue,
      categoryId: categoryIdValue,
      fromDate: fromDateValue,
    };
    return validateExpense(formDataWithExtraFields, {
      getFormFields,
    });
  }, [formData, getFormFields, watch]);

  const saveBeforeSubmit = useCallback(async () => {
    await autoSaveExpense(allFormValuesRef.current, {
      dateTo: formData.toDate
        ? formData.toDate.toISOString().split('T')[0]
        : null,
      timeFrom: formData.fromTime || null,
      timeTo: formData.toTime || null,
    });
  }, [autoSaveExpense, formData.toDate, formData.fromTime, formData.toTime]);

  const { submitExpense, isSubmitting } = useExpenseSubmit({
    expenseId,
    onSave,
    successMessage: t('expenses.perDiemForm.expenseSubmittedSuccessfully'),
    errorMessage: t('expenses.perDiemForm.expenseSubmitFailed'),
    validateBeforeSubmit: validateExpenseCallback,
    saveBeforeSubmit,
  });

  useEffect(() => {
    if (!expenseData?.expense || hasLoadedInitialData) return;

    const expense = expenseData.expense;
    const initialFormData = mapExpenseToFormData(expense);
    setFormData(initialFormData);

    const mealExpenseItem = getMealExpenseItem(expense.expenseItems);
    const newAllowances = initializeMealAllowances(mealExpenseItem, t);
    setMealAllowances(newAllowances);
    setPreviousMealAllowances(newAllowances);

    setHasLoadedInitialData(true);
    isFirstRenderAfterLoadRef.current = true;

    lastSavedDataRef.current = {
      formData: initialFormData,
      extraFieldsString: '',
    };
  }, [
    expenseData,
    hasLoadedInitialData,
    setFormData,
    setHasLoadedInitialData,
    t,
    lastSavedDataRef,
  ]);

  const currentAllowances = isUpdatingExpenseItem
    ? previousMealAllowances
    : mealAllowances;

  const hasNoMealAllowanceRules = useMemo(
    () =>
      expenseFormData && !hasMealAllowanceRules(expenseFormData?.expenseRules),
    [expenseFormData],
  );

  const mealDeductions = useMemo((): MealDeductions => {
    if (!expenseFormData?.expenseRules) {
      return { breakfast: 0, lunch: 0, dinner: 0 };
    }

    const mealExpenseItem = getMealExpenseItem(
      expenseData?.expense?.expenseItems,
    );
    const percentages = getMealPercentages(expenseFormData.expenseRules);
    const includedPercentage = calculateIncludedPercentage(
      currentAllowances,
      percentages,
    );

    if (includedPercentage === 0) {
      return { breakfast: 0, lunch: 0, dinner: 0 };
    }

    const fullAmount =
      mealExpenseItem?.flair__Manual_Amount__c ??
      expenseData?.expense?.flair__Final_Amount__c ??
      0;

    return calculateMealDeductions(fullAmount, includedPercentage, percentages);
  }, [
    expenseFormData?.expenseRules,
    expenseData?.expense?.flair__Final_Amount__c,
    expenseData?.expense?.expenseItems,
    currentAllowances,
  ]);

  const handleMealAllowanceChange = useCallback(
    async (type: MealType, checked: boolean) => {
      setPreviousMealAllowances(mealAllowances);

      setMealAllowances((prev) =>
        prev.map((meal) => (meal.type === type ? { ...meal, checked } : meal)),
      );

      if (!expenseId || !expenseData?.expense?.expenseItems) return;

      const mealExpenseItem = getMealExpenseItem(
        expenseData.expense.expenseItems,
      );
      if (!mealExpenseItem) return;

      setIsUpdatingExpenseItem(true);
      try {
        await updateExpenseItemMutation({
          variables: {
            input: buildMealAllowanceUpdateInput(
              mealExpenseItem,
              type,
              checked,
            ),
          } as UpdateExpenseItemMutationVariables,
        });
      } catch (error) {
        console.error('Error updating expense item:', error);
        addError(t('expenses.perDiemForm.expenseItemUpdateFailed'));
      } finally {
        setIsUpdatingExpenseItem(false);
      }
    },
    [
      mealAllowances,
      expenseId,
      expenseData?.expense?.expenseItems,
      updateExpenseItemMutation,
      addError,
      t,
    ],
  );

  const handleSubmit = useCallback(async () => {
    await submitExpense();
  }, [submitExpense]);

  const extraFieldsString = useMemo(() => {
    if (!allFormValuesWatched) return '';
    const extraFields = collectFormDataExtraFields(allFormValuesWatched);
    return JSON.stringify(extraFields);
  }, [allFormValuesWatched]);

  useEffect(() => {
    if (!hasLoadedInitialData || !expenseId) return;

    if (isFirstRenderAfterLoadRef.current) {
      isFirstRenderAfterLoadRef.current = false;
      previousExtraFieldsRef.current = extraFieldsString;
      return;
    }

    previousExtraFieldsRef.current = extraFieldsString;

    const dataChanged = hasDataChanged(formData, extraFieldsString);

    if (!dataChanged) return;

    const timerId = setTimeout(async () => {
      await autoSaveExpense(allFormValuesRef.current, {
        dateTo: formData.toDate
          ? formData.toDate.toISOString().split('T')[0]
          : null,
        timeFrom: formData.fromTime || null,
        timeTo: formData.toTime || null,
      });
    }, AUTO_SAVE_DELAY_MS);

    return () => clearTimeout(timerId);
  }, [
    formData,
    mealAllowances,
    expenseId,
    hasLoadedInitialData,
    extraFieldsString,
    hasDataChanged,
    autoSaveExpense,
  ]);

  return (
    <div className="per-diem-form">
      <div>
        <DynamicExpenseForm
          onValueChange={handleInputChange}
          displayFullDateTime={true}
          values={{
            description: formData.description,
            date: formData.fromDate,
            categoryId: formData.categoryId || formData.category || '',
            merchantId: formData.merchantId || formData.merchant || '',
            projectId: formData.projectId || formData.project || '',
            expenseGroupId:
              formData.expenseGroupId || formData.expenseGroup || '',
            dateTo: formData.toDate,
            timeFrom: formData.fromTime,
            timeTo: formData.toTime,
          }}
          options={{
            categoryOptions,
            projectOptions,
            expenseGroupOptions,
            merchantOptions,
          }}
          onCreateMerchant={handleCreateMerchant}
          dateLabel={t('expenses.fields.dateFromLabel')}
          dateValidationError={null}
          categoryRequired={true}
          merchantRequired={false}
        />

        <div className="d-flex align-items-center gap-2 mt-4 mb-4">
          <h4 className="mb-0">{t('expenses.fields.mealAllowanceLabel')}</h4>
          <OverlayTrigger
            overlay={
              <Tooltip id="meal-allowance-info-tooltip">
                {t('expenses.perDiemForm.mealAllowanceTooltip')}
              </Tooltip>
            }
            placement="top">
            <span>
              <FlairIcon
                icon="information-circle-outline"
                size="sm"
                className="infoIcon"
              />
            </span>
          </OverlayTrigger>
        </div>

        {hasNoMealAllowanceRules && (
          <Alert variant="warning" className="mb-4">
            {t('expenses.perDiemForm.noMealAllowanceRulesWarning')}
          </Alert>
        )}

        {mealAllowances.map((meal) => (
          <Form.Group
            key={meal.type}
            className="mb-3 d-flex justify-content-between align-items-center">
            <Form.Check
              type="checkbox"
              id={`meal-${meal.type}`}
              label={meal.label}
              checked={meal.checked}
              className={
                isUpdatingExpenseItem ? 'per-diem-checkbox-disabled' : ''
              }
              onChange={(e) =>
                !isUpdatingExpenseItem &&
                handleMealAllowanceChange(meal.type, e.target.checked)
              }
            />
            <div className="text-end">
              <span
                className={`fw-bold ${
                  meal.checked ? 'text-primary' : 'text-muted'
                }`}>
                {expenseData?.expense?.CurrencyIsoCode
                  ? formatCurrency({
                      value: mealDeductions[meal.type],
                      currency: expenseData.expense.CurrencyIsoCode,
                    })
                  : `€${mealDeductions[meal.type].toFixed(2)}`}
              </span>
            </div>
          </Form.Group>
        ))}

        <hr />

        <FinalAmountDisplay
          finalAmount={expenseData?.expense?.flair__Final_Amount__c}
          currencyCode={expenseData?.expense?.CurrencyIsoCode}
          formatCurrency={formatCurrency}
        />

        <SharedFormButtons
          onCancel={onCancel}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          isUpdating={isUpdatingExpense || isUpdatingExpenseItem}
          submitDisabled={!!(formData.expenseGroupId || formData.expenseGroup)}
          submitDisabledTooltip={t(
            'expenses.perDiemForm.submitDisabledTooltip',
          )}
          showGroupWarning={
            !!(formData.expenseGroupId || formData.expenseGroup)
          }
        />
      </div>
    </div>
  );
};

export const PerDiemForm: React.FC<Props> = (props) => {
  const getDefaultValues = (
    expense: Expense | null,
    defaultExpenseGroupId?: string,
  ): FormValues => {
    const expenseGroupId =
      expense?.flair__Expense_Group__c || defaultExpenseGroupId || '';
    const projectId = expense?.project?.Id || '';
    const categoryId = expense?.expenseCategory?.Id || '';
    const merchantId = expense?.merchant?.Id || '';

    if (!expense) {
      return {
        [recordTypeFormKey]: PER_DIEM_RECORD_TYPE_KEY,
        description: '',
        fromDate: new Date(),
        toDate: null,
        fromTime: '',
        toTime: '',
        expenseGroup: expenseGroupId || null,
        project: null,
        category: null,
        merchant: null,
        expenseGroupId: expenseGroupId || '',
        projectId: '',
        categoryId: '',
        merchantId: '',
      };
    }

    return {
      [recordTypeFormKey]: PER_DIEM_RECORD_TYPE_KEY,
      description: expense.flair__Description__c || '',
      fromDate: expense.flair__Date_From__c
        ? new Date(expense.flair__Date_From__c)
        : new Date(),
      toDate: expense?.flair__Date_To__c
        ? new Date(expense.flair__Date_To__c)
        : null,
      fromTime: normalizeTimeToHHmm(expense.flair__Time_From__c) || '',
      toTime: normalizeTimeToHHmm(expense.flair__Time_To__c) || '',
      expenseGroup: expenseGroupId || null,
      project: projectId || null,
      category: categoryId || null,
      merchant: merchantId || null,
      expenseGroupId,
      projectId,
      categoryId,
      merchantId,
    };
  };

  const getValidationSchema = () => {
    return yup.object().shape({});
  };

  return (
    <ExpenseFormWrapper<FormValues>
      recordType={ExpenseRecordType.PerDiem}
      wrapperProps={props}
      getDefaultValues={getDefaultValues}
      getValidationSchema={getValidationSchema}>
      <PerDiemFormContent {...props} />
    </ExpenseFormWrapper>
  );
};
